import { FEATURE_FLAGS } from "@solomon-apps/config";
import type { YaasPositionCardData } from "@solomon-apps/shared/src/types/yaas";
import { lpProtocolLabel } from "@solomon-apps/shared/src/yaas/lpProtocols";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import { withPositionAmount } from "./helpers";
import type { RaydiumLpHolding } from "./lpTypes";
import { matchesLpHolding, unmatchedLpHoldings } from "./raydiumLpHoldings";
import { useRaydiumLpBalances } from "./useRaydiumLpBalances";
import { useRaydiumLpHoldings } from "./useRaydiumLpHoldings";
import { useWalletView } from "./useWalletView";
import { walletLpSnapshot } from "./walletView";
import { holdingKey } from "./yaasLpHoldings";

interface RetailLpPositionsResult extends Omit<ReturnType<typeof useRaydiumLpHoldings>, "incomplete"> {
    positions: YaasPositionCardData[];
    /** No wallet observation to total from. An LP whose own amount is unknown
     *  leaves the rest usable: the total reports it as partial instead. */
    balancesUnavailable: boolean;
}

/** Explicit provider selection. Both RPC implementations stay available for rollback. */
export function useRetailLpPositions(
    walletAddress: string | null | undefined,
    positions: YaasPositionCardData[],
    enabled: boolean,
    provider: "rpc" | "yaas" = FEATURE_FLAGS.retailLpApiDiscovery ? "yaas" : "rpc",
    accountAddress: string | null | undefined = walletAddress
): RetailLpPositionsResult {
    const rpcPositions = useRaydiumLpBalances(positions, walletAddress, enabled && provider === "rpc");
    const rpcHoldings = useRaydiumLpHoldings(
        walletAddress,
        positions,
        enabled && provider === "rpc" && FEATURE_FLAGS.retailRaydiumLpFallback
    );
    const active = enabled && !!walletAddress && provider === "yaas";
    const query = useWalletView(accountAddress, active);
    if (provider === "rpc") {
        const { incomplete, ...rest } = rpcHoldings;
        return { positions: rpcPositions, ...rest, balancesUnavailable: incomplete };
    }
    const snapshot =
        active && query.data?.observation.ownerAddress === walletAddress ? walletLpSnapshot(query.data) : undefined;
    const observations = snapshot?.holdings ?? [];
    const holdings: RaydiumLpHolding[] = observations.map((row) => ({
        key: holdingKey(row),
        positionAddress: row.positionAddress,
        amount: row.amount,
        pool: {
            protocol: row.protocol,
            poolAddress: row.poolAddress,
            observedAssetMint: row.observedAssetMint,
            displayName: row.pairLabel ?? lpProtocolLabel(row.protocol)
        }
    }));
    const enriched = active
        ? positions.map((position) => {
              const identity = position.position.lpIdentity;
              if (!identity) return position;
              const row = observations.find((holding) => matchesLpHolding(identity, holding));
              // A scan that omits the LP proves nothing, so its last persisted observation stands in.
              return withPositionAmount(position, row?.amount ?? position.position.lastObservedUsdvAmount ?? null);
          })
        : positions;
    return {
        positions: enriched,
        balancesUnavailable: active && !snapshot,
        holdings: unmatchedLpHoldings(holdings, positions).filter(
            (holding) => holding.amount === null || new BigDecimal(holding.amount).gt(0)
        ),
        error: active
            ? snapshot
                ? null
                : query.data?.observation.status === "unavailable"
                  ? new Error("Wallet discovery is unavailable. Try again shortly.")
                  : query.error
            : null,
        isFetching: active && query.isFetching,
        refetch: async (): Promise<void> => {
            if (active) await query.refetch();
        }
    };
}
